import React, { useEffect, useState, useRef, useCallback, forwardRef, useImperativeHandle } from "react";

import * as S from "./styles";
import KogCheckbox from "../../checkbox/kogCheckbox";
import BoxGrid from "../../boxGrid/boxGrid";
import { getDefaultMessageResultsFound, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { KeyValue } from "@/main/models/common";
import { supervisionAssignmentFilterRequest } from "@/main/models/request/supervisionAssignment";
import { supervisionAssignmentApi } from "@/main/api/supervisionAssignmentApi";

interface IKOGInsurerCompanyBoxGridMultiCheckboxProps {
	rowHeaders: string[] | React.ReactNode[];
	dataBodyshops: KeyValue[];
	guidCompany: string;
	guidInsurer?: string;
	isStickHeader?: boolean;
	isLoading: boolean;
	singleSelect?: boolean;
	initialFilters: supervisionAssignmentFilterRequest | null;
	intialTotalItems: number;
	onChanged?: (selected: string) => void;
	onEventCallback?: () => void;
}

export interface KOGInsurerCompanyBoxGridMultiCheckboxRef {
	getSelectedItems: () => string[];
}

interface IGridData {
	data: KeyValue[];
	totalItems: number;
	isLoading: boolean;
}

const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};

const ID_SCROLLABLE_TARGET = "id-scrollable-target-myBodyshopsSidebar";

const KOGInsurerCompanyBoxGridMultiCheckbox = forwardRef<
	KOGInsurerCompanyBoxGridMultiCheckboxRef,
	IKOGInsurerCompanyBoxGridMultiCheckboxProps
>(
	(
		{
			dataBodyshops,
			guidCompany,
			guidInsurer,
			singleSelect = false,
			initialFilters,
			intialTotalItems,
			onChanged,
			onEventCallback,
		},
		ref,
	) => {
		const [gridData, setGridData] = useState<IGridData>({
			...INITIAL_GRID_STATE,
			data: dataBodyshops,
			totalItems: dataBodyshops.length,
		});
		const [selectedItems, setSelectedItems] = useState<string[]>([]);
		const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);
		const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<boolean>(false);
		const [hasShowMore, setHasShowMore] = useState<boolean>(false);
		const observer = useRef<IntersectionObserver>();
		const [totalRecords, setTotalRecords] = useState<number>(0);
		const titleGrid = getDefaultMessageResultsFound(gridData?.data.length, totalRecords);

		useImperativeHandle(ref, () => ({
			getSelectedItems: () => selectedItems,
		}));

		const onLoadMore = useCallback(
			async (pageIndex: number): Promise<KeyValue[]> => {
				const filters: supervisionAssignmentFilterRequest = {
					...(isNullUndefined(initialFilters) ? {} : initialFilters),
					guidCompany,
					guidInsurer,
					pageIndex,
					pageSize: PaginationEnum.PageSize15,
				};

				const result = await supervisionAssignmentApi.getbodyshopinsurerbyfilter(filters);

				setTotalRecords(result.data?.result?.bodyshops?.totalNumberOfRecords);

				return result.data.result.bodyshops.results;
			},
			[guidCompany, guidInsurer, initialFilters],
		);

		const loadMoreData = async () => {
			if (!onLoadMore || isLoadingInfiniteScroll) return;

			setIsLoadingInfiniteScroll(true);
			try {
				const nextPage = pageIndex + 1;
				const newData = await onLoadMore(nextPage);

				if (newData && newData.length > 0) {
					setGridData((prev) => ({
						...prev,
						data: [...prev.data, ...newData],
						totalItems: prev.totalItems + newData.length,
					}));
					setPageIndex(nextPage);
					setHasShowMore(newData.length === PaginationEnum.PageSize15);
				} else {
					setHasShowMore(false);
				}
			} catch (error) {
				console.error("Erro ao carregar mais dados:", error);
			} finally {
				setIsLoadingInfiniteScroll(false);
			}
		};

		const loadingRef = useCallback(
			(node: HTMLDivElement | null) => {
				if (!node || isLoadingInfiniteScroll || !hasShowMore) return;

				if (observer.current) observer.current.disconnect();

				observer.current = new IntersectionObserver((entries) => {
					if (entries[0].isIntersecting) {
						loadMoreData();
					}
				});

				observer.current.observe(node);
			},
			[isLoadingInfiniteScroll, hasShowMore, loadMoreData],
		);

		const handleToggleSelect = (index: number, checked: boolean) => {
			const insurer = gridData.data[index];
			let newSelectedItems: string[];

			if (checked) {
				newSelectedItems = singleSelect ? [String(insurer.key)] : [...selectedItems, String(insurer.key)];
			} else {
				newSelectedItems = singleSelect ? [] : selectedItems.filter((guid) => guid !== insurer.key);
			}

			setSelectedItems(newSelectedItems);

			if (singleSelect && onChanged) {
				onChanged(checked ? String(insurer.key) : "");
			}

			if (onEventCallback) {
				onEventCallback();
			}
		};

		const handleSelectAll = (checked: boolean) => {
			if (singleSelect) return;
			const newSelectedItems = checked ? gridData.data.map((insurer) => String(insurer.key)) : [];
			setSelectedItems(newSelectedItems);

			if (onEventCallback) onEventCallback();
		};

		useEffect(() => {
			setGridData({
				...INITIAL_GRID_STATE,
				data: dataBodyshops,
				totalItems: dataBodyshops.length,
			});
			setSelectedItems([]);
			setTotalRecords(intialTotalItems);
			setPageIndex(PaginationEnum.PageIndexIntial);
			setHasShowMore(dataBodyshops.length === PaginationEnum.PageSize15);
		}, [dataBodyshops]);

		const rowHeadersInsurerCompany = (
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					minHeight: "2.5rem",
				}}
			>
				<S.TitleType>{getTranslateByKey("information.rfcBodyshop")}</S.TitleType>
				{!singleSelect && (
					<KogCheckbox
						checked={selectedItems.length === gridData.data.length && gridData.data.length > 0}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							handleSelectAll(event.target.checked);
						}}
						id="id-registerBodyshops-checkbox-selectAll"
					/>
				)}
			</div>
		);

		const rowContentInsurerCompany = gridData.data.map((insurer, index) => (
			<S.ContainerHeaderGrid key={`${insurer.key}-${index}`} style={{ backgroundColor: "white" }}>
				<S.TitleType>{insurer.value}</S.TitleType>
				<KogCheckbox
					checked={selectedItems.includes(String(insurer.key))}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
						handleToggleSelect(index, event.target.checked);
					}}
					id={`id-registerBodyshops-checkbox-${insurer.key}`}
				/>
			</S.ContainerHeaderGrid>
		));

		return (
			<S.ContainerScroll id={ID_SCROLLABLE_TARGET}>
				<S.TitlerCount>{titleGrid}</S.TitlerCount>
				<BoxGrid
					marginCustom="0.5rem 0 0 0"
					rowHeaders={[rowHeadersInsurerCompany]}
					typeGrid="default"
					propsTable={{ stickyHeader: true }}
					sizeCell="small"
				>
					{rowContentInsurerCompany}
					{hasShowMore && (
						<div ref={loadingRef} style={{ padding: "16px", textAlign: "center" }}>
							{isLoadingInfiniteScroll && <S.Spinner />}
						</div>
					)}
				</BoxGrid>
			</S.ContainerScroll>
		);
	},
);

export default KOGInsurerCompanyBoxGridMultiCheckbox;
